export interface PhotoSize {
  id: string
  label: string
  dimensions: string
  price: number
  edition: string
  editionTotal: number
  remaining: number
  frameUpcharge: number
}

export interface Photo {
  id: string
  title: string
  collection: string
  description: string
  narrativeDescription: string
  location: string
  year: number
  imageUrl: string
  sizes: PhotoSize[]
  category: 'shadow' | 'awareness' | 'light'
}

export const collections: Record<string, string> = {
  all: 'Ten images that mark moments along an inner journey, where light and shadow coexist, as they do within us.',
  shadow:
    'The beginning of the descent. These images explore the moment of turning inward — confronting darkness, fear, and the unknown.',
  awareness:
    'The slow recognition that you are not alone in this process. Shadows belong to everyone, and seeing them clearly is the first step.',
  light:
    'The gradual return. Not a sudden illumination, but the quiet understanding that progress is already happening, even before you can see it.',
}

const generateSizes = (baseRemaining: number[]): PhotoSize[] => [
  {
    id: 's',
    label: 'Small',
    dimensions: '16" × 20"',
    price: 190,
    edition: 'Limited Edition of 50',
    editionTotal: 50,
    remaining: baseRemaining[0],
    frameUpcharge: 200,
  },
  {
    id: 'm',
    label: 'Medium',
    dimensions: '24" × 36"',
    price: 490,
    edition: 'Limited Edition of 25',
    editionTotal: 25,
    remaining: baseRemaining[1],
    frameUpcharge: 300,
  },
  {
    id: 'l',
    label: 'Large',
    dimensions: '40" × 60"',
    price: 990,
    edition: 'Limited Edition of 15',
    editionTotal: 15,
    remaining: baseRemaining[2],
    frameUpcharge: 400,
  },
]

export const photos: Photo[] = [
  {
    id: '1',
    title: 'Descent Into My Own Shadow',
    collection: 'Entre Luces y Sombras',
    description:
      'A descending staircase of concrete and geometry, a solitary figure walking with his shadow ahead of him.',
    narrativeDescription:
      'This was one of my first photographs in my professional path. I did not take it knowing that it would one day open a series about the deepest introspective journey of my life. When I started this project, I found it in my archive and immediately recognized the connection to it. The concrete, the geometry, the descending staircase toward a person with his shadow in front of him. It had already captured what I had not yet lived.\n\nI have always been a reflective person. But this was different. Something led me inward. What followed was a long process, one that is still unfolding. I encountered fears I had avoided, patterns inherited from others, and a way of seeing the world that was not entirely mine.\n\nSomeone once told me that an artist creates their work when they have something real to say. I had something to say. Ten images. Each one a stop along that inner path.',
    location: 'Montreal, Canada',
    year: 2010,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/8pw1QccqNAo5mL7cxFbV9Y/Descenso_a_mi_propia_sombra.jpg',
    sizes: generateSizes([40, 20, 12]),
    category: 'shadow',
  },
  {
    id: '2',
    title: 'Darkness',
    collection: 'Entre Luces y Sombras',
    description:
      'A child runs through a sun-drenched plaza in silhouette, a monumental statue looming above, the light blinding more than it guides.',
    narrativeDescription:
      'By the time I took this photograph, I already knew what I was building. I traveled to Luján, a city where millions come seeking comfort and light, home to the minor basilica dedicated to our Lady of Luján, the patron saint of Argentina, and I went looking for darkness.\n\nNot the darkness of despair. The kind that surrounds you when you are deep in your own process, when the light exists but blinds more than it guides. People come to Luján for answers. I came for the moment before any answer arrives.',
    location: 'Luján, Argentina',
    year: 2025,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/7yASFx9Qde7Pg3vECX7Cwm/Oscuridad.jpg',
    sizes: generateSizes([45, 22, 14]),
    category: 'shadow',
  },
  {
    id: '3',
    title: 'Uncertainty',
    collection: 'Entre Luces y Sombras',
    description:
      'A narrow stone corridor frames distant figures — a man reaching toward a stroller, a mother, a child — the scene ambiguous in the surrounding dark.',
    narrativeDescription:
      'I was walking through a narrow stone corridor when I saw them at the far end. A man reaching toward a stroller. A mother. A child. The alley was almost entirely dark and I could not tell what I was seeing. A father helping, or something wrong.\n\nThe uncertainty came instantly, and with it a quiet fear. I raised the camera before I understood why. That feeling of not being able to trust what you see, of standing in the dark and not knowing, was something I had been living for months. Not out there. In here.',
    location: 'Segovia, Spain',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/6tSzuWc4TFDmf36ca9MjFp/Incertidumbre.jpg',
    sizes: generateSizes([38, 18, 10]),
    category: 'shadow',
  },
  {
    id: '4',
    title: 'Not Only My Shadow',
    collection: 'Entre Luces y Sombras',
    description:
      'A woman walks along a coastal path, her shadow stretching ahead, when a second unexpected shadow enters the frame.',
    narrativeDescription:
      'I was standing on the coast looking at the horizon when I noticed her: a woman walking through the midday light, a strong shadow stretching ahead. I raised the camera. At the exact moment I pressed the shutter, a second shadow entered the frame, from someone just out of sight. I had not seen it coming.',
    location: 'Polignano a Mare, Italy',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/uEwcRMh9Co1npMquxe6E7M/No_es_solo_mi_sombra.jpg',
    sizes: generateSizes([35, 15, 8]),
    category: 'awareness',
  },
  {
    id: '5',
    title: 'I Am Not Alone',
    collection: 'Entre Luces y Sombras',
    description:
      'A long line of people waiting quietly on a train platform, perfectly arranged, each casting their shadow onto the polished floor.',
    narrativeDescription:
      'What struck me first was the order. A long line of people waiting quietly for their train, perfectly arranged, each in their place. Tokyo gives that impression, that everything is under control.\n\nThen I noticed the shadows. Every single person casting their reflection onto the floor, whether they wanted to or not.',
    location: 'Tokyo, Japan',
    year: 2016,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/hKJzoHkYsdJyAhUVc7cHTE/No_soy_el_unico.jpg',
    sizes: generateSizes([30, 12, 6]),
    category: 'awareness',
  },
  {
    id: '6',
    title: 'Reflection',
    collection: 'Entre Luces y Sombras',
    description:
      'A woman moves alone through a narrow street, unhurried, the light catching her hair in the surrounding dark.',
    narrativeDescription:
      'We had been walking with friends through the old city. At some point I turned around and there she was: a woman moving alone through the narrow street, unhurried, the light catching her hair in the surrounding dark. Something in her pace stopped me. She was not going anywhere urgently. She was thinking.\n\nI did not hesitate. One frame, instinctive, done. For months I had been doing the same thing. Moving through the world surrounded by people, while inside I was walking very slowly, turning things over.\n\nReflection is not something you decide to do. It just happens, when you have been honest with yourself long enough.',
    location: 'Segovia, Spain',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/sv2aC1aFTYrkSDUkZVttWY/Reflexion.jpg',
    sizes: generateSizes([42, 20, 10]),
    category: 'awareness',
  },
  {
    id: '7',
    title: 'One Step From the Light',
    collection: 'Entre Luces y Sombras',
    description:
      'A woman walks at the edge of shadow and light on an Italian street corner, one step away from full illumination.',
    narrativeDescription:
      "There are days when you feel like nothing is moving. Like you are stuck, still carrying the same weight, still asking the same questions.\n\nWhat I came to understand, slowly, is that those are often the moments when you are closest to something changing. You are working through it without being able to see it yet. Progress does not always announce itself. It's a process.",
    location: 'Polignano a Mare, Italy',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/dznRbnZueDMgTN6RXyTcGA/A_un_paso_de_la_luz.jpg',
    sizes: generateSizes([36, 16, 9]),
    category: 'light',
  },
  {
    id: '8',
    title: 'Illuminated',
    collection: 'Entre Luces y Sombras',
    description:
      'Inside a vast cathedral, a single beam of light descends from above onto a solitary figure seated among the pews.',
    narrativeDescription:
      'At some point in the process, something shifts. Not dramatically. A fear you have been carrying begins to feel lighter. Something that once seemed impossible starts to feel less so.\n\nI found this image inside a church in Segovia, a space shaped by faith, where people bring their darkness and leave with the possibility of light. Two figures stand in the distance, small within the shadows, held in a single beam descending from above.\n\nThat is what it felt like. Not a flood of light, but a narrow beam. Enough to keep going, enough to begin trusting that something is working, even when you cannot yet see it.',
    location: 'Segovia, Spain',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/omkhEuMw16L9itvPrRT5bC/Iluminada.jpg',
    sizes: generateSizes([25, 10, 4]),
    category: 'light',
  },
  {
    id: '9',
    title: 'Better Together',
    collection: 'Entre Luces y Sombras',
    description:
      'Two people sit at the edge of a pier, leaning into each other, their shadows merging into a single shape on the ground.',
    narrativeDescription:
      'What caught my eye was the symmetry. Two people, two cranes, two port structures on either side of the water. They were sitting at the edge of the pier, leaning into each other, their shadows merging into a single shape on the ground.\n\nThat image held something I had been learning throughout the process. You can go through it alone. But it is so much harder. When you have someone beside you, someone you can ask for support without feeling like you should already have it figured out, everything becomes more bearable. The light feels closer.\n\nMy wife has been that person for me. She does not carry it for me. She just stays.',
    location: 'Dubrovnik, Croatia',
    year: 2024,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/2wzn15TN9bgBXWvDSStxUh/Acompanado_es_mejor.jpg',
    sizes: generateSizes([32, 14, 7]),
    category: 'light',
  },
  {
    id: '10',
    title: 'Between Light and Shadow',
    collection: 'Entre Luces y Sombras',
    description:
      'A dancer moves in a shaft of light inside Grand Central Terminal, fluid and undisturbed, while hundreds pass around her.',
    narrativeDescription:
      'I was walking through the station when I saw her. She had found the only shaft of light coming through the ceiling, and she was dancing in it. Around her, hundreds of people moved in every direction, absorbed in their routines, their destinations, their ordinary day.\n\nThis is what the whole journey was for. Not to eliminate the darkness, not to arrive at some permanent brightness where fear no longer exists, but to learn to move the way she moved, like water, fluid and undisturbed by what surrounds it, always finding its way toward the light without forcing it.\n\nTo seek the light is to keep moving despite fear, to look for the clarity that allows you to connect what you feel, what you think, and what you do. When those three align, even briefly, even imperfectly, that is what it means to live between light and shadow.',
    location: 'New York, United States',
    year: 2019,
    imageUrl:
      'https://cdn.magicpatterns.com/uploads/wfdb3QPjHSbppY1TiHiBPM/Entre_luces_y_sombras.jpg',
    sizes: generateSizes([20, 8, 3]),
    category: 'light',
  },
]
